import React, { useState, useMemo, useEffect } from 'react';
import {
    Search,
    Filter,
    ChevronUp,
    ChevronDown,
    Edit,
    Trash2,
    Eye,
    ArrowUpDown
} from 'lucide-react';
import { useTranslation } from "react-i18next";


// Composant Tableau Réutilisable
const ReusableTable = ({
    data = [],
    columns = [],
    onEdit,
    onDelete,
    onDetails,
    onRowClick,
    colors = {},
    itemsPerPageOptions = [10, 25, 50, 100]
}) => {

   const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filterColumn, setFilterColumn] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageOptions[0]);

    // Couleurs par défaut avec possibilité de personnalisation
    const themeColors = {
        headerBg: colors.headerBg || '#2c3e50',
        headerText: colors.headerText || '#ffffff',
        rowEven: colors.rowEven || '#ffffff',
        rowOdd: colors.rowOdd || '#f8f9fa',
        rowHover: colors.rowHover || '#00f2fd',
        border: colors.border || '#d1e8f3ff',
        searchBg: colors.searchBg || '#ffffff',
        buttonEdit: colors.buttonEdit || '#3498db',
        buttonDelete: colors.buttonDelete || '#e74c3c',
        buttonDetails: colors.buttonDetails || '#2ecc71',
        expandedBg: colors.expandedBg || '#f0f8ff',
        paginatebg: colors.paginatebg || '#edeafdff'
    };

    // Filtrage et recherche
    const filteredData = useMemo(() => {
        return data.filter(item => {
            const searchValue = searchTerm.toLowerCase();

            if (filterColumn === 'all') {
                return columns.some(col =>
                    String(item[col.key]).toLowerCase().includes(searchValue)
                );
            } else {
                return String(item[filterColumn]).toLowerCase().includes(searchValue);
            }
        });
    }, [data, searchTerm, filterColumn, columns]);

    // Tri
    const sortedData = useMemo(() => {
        let sortableItems = [...filteredData];

        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Tente de convertir en nombre si ce n'est pas une chaîne
                if (typeof aValue !== 'string' || (typeof aValue === 'string' && !isNaN(aValue))) {
                    aValue = parseFloat(aValue) || 0;
                }
                if (typeof bValue !== 'string' || (typeof bValue === 'string' && !isNaN(bValue))) {
                    bValue = parseFloat(bValue) || 0;
                }

                // Gérer les chaînes composées (ex: "125.50 EUR")
                if (typeof aValue === 'string' && (aValue.includes('€') || aValue.includes('EUR'))) {
                    aValue = parseFloat(aValue.replace(/[^0-9.-]+/g, "")) || 0;
                }
                if (typeof bValue === 'string' && (bValue.includes('€') || bValue.includes('EUR'))) {
                    bValue = parseFloat(bValue.replace(/[^0-9.-]+/g, "")) || 0;
                }

                if (typeof aValue === 'string' && (aValue.includes('%'))) {
                    aValue = parseFloat(aValue.replace(/[^0-9.-]+/g, "")) || 0;
                }
                if (typeof bValue === 'string' && (bValue.includes('%'))) {
                    bValue = parseFloat(bValue.replace(/[^0-9.-]+/g, "")) || 0;
                }

                // Si les valeurs sont des nombres, on compare directement
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    if (aValue < bValue) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                }

                // Si les valeurs sont des chaînes (après normalisation pour les cas non numériques), on compare en minuscule
                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                }
                if (typeof bValue === 'string') {
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return sortableItems;
    }, [filteredData, sortConfig]);

    // Gestion du tri
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Rendu responsive conditionnel
    const isMobile = window.innerWidth < 768;

    // Pagination
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    // Reset à la page 1 quand les données ou les filtres changent
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterColumn, sortConfig]);

    // Gestionnaires de pagination
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value) => {
        setItemsPerPage(Number(value));
        setCurrentPage(1);
    };

    // Générer les numéros de page à afficher
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    return (
        <div className="p-2" style={{ maxWidth: '100%' }}>
            {/* Barre de contrôle - Toujours sur la même ligne */}
            <div className="d-flex gap-3 mb-4 align-items-center justify-content-between flex-wrap">
                <div className="position-relative flex-grow-1" style={{ maxWidth: '800px' }}>
                    <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-control pe-5"
                        style={{
                            backgroundColor: themeColors.searchBg,
                            borderRadius: '6px'
                        }}
                    />
                    <Search size={16} className="position-absolute text-secondary" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                </div>

                <div className="d-flex gap-2 align-items-center">
                    <Filter size={14} className="text-secondary" />
                    <select
                        value={filterColumn}
                        onChange={(e) => setFilterColumn(e.target.value)}
                        className="form-select"
                        style={{
                            backgroundColor: themeColors.searchBg,
                            borderRadius: '6px',
                            minWidth: '150px'
                        }}
                    >
                        <option value="all">{t('allColumns')}</option>
                        {columns.map(col => (
                            <option key={col.key} value={col.key}>
                                {col.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tableau */}
            <div className="overflow-auto rounded-2 mb-2">
                <table className="table table-hover mb-0 bg-white shadow-sm" style={{ minWidth: '1000px', fontSize: '14px' }}>
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    className="text-white text-center align-middle fw-semibold py-3 px-2 border-0 user-select-none"                                    style={{
                                        backgroundColor: themeColors.headerBg,
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => handleSort(col.key)}
                                >
                                    <div className="d-flex justify-content-center align-items-center gap-1">
                                        <span>{col.label}</span>
                                        <div className="d-flex align-items-center">
                                            {sortConfig.key === col.key ? (
                                                sortConfig.direction === 'asc' ?
                                                    <ChevronUp size={14} /> :
                                                    <ChevronDown size={14} />
                                            ) : (
                                                <ArrowUpDown size={12} opacity={0.5} />
                                            )}
                                        </div>
                                    </div>
                                </th>
                            ))}
                            <th
                                className="text-white text-center fw-semibold py-3 px-2 border-0"
                                style={{
                                    backgroundColor: themeColors.headerBg,
                                    width: '120px'
                                }}
                            >
                                {t('actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + 2} className="text-center py-5 text-muted fst-italic">
                                    {t('noData') || "Aucune donnée à afficher"}
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map((row, index) => (
                                <React.Fragment key={index}>
                                    <tr
                                        onClick={() => onRowClick && onRowClick(row)}
                                        style={{
                                            cursor: onRowClick ? 'pointer' : 'default',
                                            transition: 'background-color 0.2s ease',
                                        }}
                                    >
                                        {columns.map(col => (
                                            <td key={col.key} className="text-center align-middle py-3 px-2 border-bottom">
                                                {col.render
                                                    ? col.render(row[col.key], row) // rendu stylisé
                                                    : row[col.key] // rendu par défaut
                                                }
                                            </td>
                                        ))}

                                        <td className="py-3 px-2 border-bottom align-middle">
                                            <div className="d-flex gap-1 flex-nowrap justify-content-start">
                                                {onEdit && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onEdit(row);
                                                        }}
                                                        className="btn btn-sm text-white d-flex align-items-center gap-1"
                                                        style={{
                                                            backgroundColor: themeColors.buttonEdit,
                                                            padding: '4px 8px',
                                                            fontSize: '12px'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                        title={t('edit')}
                                                    >
                                                        <Edit size={12} />
                                                        {!isMobile && <span>{t('edit')}</span>}
                                                    </button>
                                                )}
                                                {onDelete && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDelete(row);
                                                        }}
                                                        className="btn btn-sm text-white d-flex align-items-center gap-1"
                                                        style={{
                                                            backgroundColor: themeColors.buttonDelete,
                                                            padding: '4px 8px',
                                                            fontSize: '12px'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                        title={t('delete')}
                                                    >
                                                        <Trash2 size={12} />
                                                        {!isMobile && <span>{t('delete')}</span>}
                                                    </button>
                                                )}
                                                {onDetails && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDetails(row);
                                                        }}
                                                        className="btn btn-sm text-white d-flex align-items-center gap-1"
                                                        style={{
                                                            backgroundColor: themeColors.buttonDetails,
                                                            padding: '4px 8px',
                                                            fontSize: '12px'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                        title={t('details')}
                                                    >
                                                        <Eye size={12} />
                                                        {!isMobile && <span>{t('details')}</span>}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {sortedData.length > 0 && (
                    <div
                        className="d-flex justify-content-between align-items-center mt-3 p-3 rounded-2 flex-wrap gap-3"
                        style={{ backgroundColor: themeColors.paginatebg }}
                    >
                        {/* Section Gauche : Items par page + Compteur */}
                        <div className="d-flex align-items-center gap-2">
                            <label className="small text-secondary text-nowrap mb-0">
                                {t('itemsPerPage')}:
                            </label>

                            <select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="form-select form-select-sm w-auto" // w-auto rend le select compact
                            >
                                {itemsPerPageOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>

                            <span className="small text-secondary text-nowrap ms-2">
                                {t('showing')} {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, sortedData.length)} {t('of')} {sortedData.length}
                            </span>
                        </div>

                        {/* Section Droite : Pagination */}
                        <nav aria-label="Page navigation">
                            <div className="d-flex gap-1">
                                <button
                                    className="btn btn-sm btn-outline-secondary px-2"
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                >
                                    {t('first')}
                                </button>

                                <button
                                    className="btn btn-sm btn-outline-secondary px-2"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    {t('previous')}
                                </button>

                                {/* Numéros de page dynamiques */}
                                <div className="d-flex gap-1 mx-1">
                                    {getPageNumbers().map(page => (
                                        <button
                                            key={page}
                                            className={`btn btn-sm px-3 ${currentPage === page ? 'text-white' : ''}`} // On enlève btn-outline-secondary qui cause le texte blanc
                                            onClick={() => handlePageChange(page)}
                                            style={{
                                                backgroundColor: currentPage === page ? themeColors.headerBg : 'transparent',
                                                borderColor: themeColors.headerBg,
                                                color: currentPage === page ? 'white' : themeColors.headerBg, // Couleur initiale
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (currentPage !== page) {
                                                    e.currentTarget.style.backgroundColor = themeColors.headerBg;
                                                    e.currentTarget.style.color = 'white';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (currentPage !== page) {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.color = themeColors.headerBg;
                                                }
                                            }}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="btn btn-sm btn-outline-secondary px-2"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    {t('next')}
                                </button>

                                <button
                                    className="btn btn-sm btn-outline-secondary px-2"
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    {t('last')}
                                </button>
                            </div>
                        </nav>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReusableTable;